import { toast } from "sonner";
import { Loader } from "@/shared/ui/Loader";
import { Button } from "@/components/ui/button";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon, SendIcon, Trash2Icon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useAnnouncementPushMonitorQuery,
  useAnnouncementQuery,
  useDeleteAnnouncementMutation,
  useResendAnnouncementPushMutation,
  useUpdateAnnouncementMutation,
} from "../api/queries";
import { AnnouncementForm } from "../ui/AnnouncementForm";
import { MarketingPushMonitorPanel } from "@/shared/notifications/MarketingPushMonitorPanel";
import { confirm } from "@/shared/lib/confirm";

export function AnnouncementEditPage() {
  const { announcementId } = useParams({
    from: "/app/announcements/$announcementId/edit",
  });
  const nav = useNavigate({
    from: "/app/announcements/$announcementId/edit",
  });
  const canManage = useActionPermission(["announcements:update"]);
  const {
    data: announcement,
    isLoading,
    error,
  } = useAnnouncementQuery(announcementId ?? "");
  const updateAnnouncement = useUpdateAnnouncementMutation();
  const deleteAnnouncement = useDeleteAnnouncementMutation();
  const resendPush = useResendAnnouncementPushMutation();
  const pushMonitor = useAnnouncementPushMonitorQuery(announcementId ?? "");

  if (!announcementId) return null;

  if (isLoading && !announcement) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ຂ່າວສານ",
                to: "/app/announcements",
              },
              {
                label: "ແກ້ໄຂຂ່າວສານ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        </Main>
      </>
    );
  }

  if (error || !announcement) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ຂ່າວສານ",
                to: "/app/announcements",
              },
              {
                label: "ແກ້ໄຂຂ່າວສານ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4 text-destructive">
            ບໍ່ພົບຂ່າວສານ
          </div>
          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/announcements" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບຄືນ
          </Button>
        </Main>
      </>
    );
  }

  const handleResendPush = async () => {
    const ok = await confirm({
      title: "ສົ່ງ push ອີກຄັ້ງ",
      description: `ສົ່ງ push ໄປແອັບລູກຄ້າສຳລັບ «${announcement.title}»?`,
      actionText: "ສົ່ງ push",
      cancelText: "ຍົກເລີກ",
    });
    if (!ok) return;
    try {
      await resendPush.mutateAsync(announcementId);
      toast.success("ເພີ່ມ push ເຂົ້າຄິວແລ້ວ — worker ຈະສົ່ງໃນໄວໆ");
    } catch (e) {
      const msg =
        e instanceof Error ? e.message : "ບໍ່ສາມາດສົ່ງ push ອີກຄັ້ງໄດ້";
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: "ລຶບຂ່າວສານ",
      description: `ລຶບ «${announcement.title}»? ການກະທຳນີ້ບໍ່ສາມາດຍົກເລີກໄດ້`,
      actionText: "ລຶບ",
      cancelText: "ຍົກເລີກ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await deleteAnnouncement.mutateAsync(announcementId);
      toast.success("ລຶບຂ່າວສານແລ້ວ");
      nav({ to: "/app/announcements" });
    } catch {
      toast.error("ບໍ່ສາມາດລຶບຂ່າວສານໄດ້");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ຂ່າວສານ",
              to: "/app/announcements",
            },
            {
              label: "ແກ້ໄຂຂ່າວສານ",
            },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂຂ່າວສານ</h2>
            <p className="text-muted-foreground">{announcement.title}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/announcements" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            {canManage && (
              <Button
                variant="secondary"
                onClick={handleResendPush}
                disabled={resendPush.isPending || !announcement.isActive}
                title={
                  announcement.pushSentAt
                    ? `ສົ່ງຄັ້ງລ່າສຸດ: ${announcement.pushSentAt}`
                    : "ຍັງບໍ່ທັນສົ່ງ push"
                }
              >
                <SendIcon className="size-4" />
                ສົ່ງ push ອີກຄັ້ງ
              </Button>
            )}
            {canManage && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleteAnnouncement.isPending}
              >
                <Trash2Icon className="size-4" />
                ລຶບ
              </Button>
            )}
          </div>
        </div>

        <AnnouncementForm
          syncKey={announcement.updatedAt}
          defaultValues={{
            title: announcement.title,
            summary: announcement.summary,
            body: announcement.body,
            imageUrl: announcement.imageUrl,
            priority: announcement.priority,
            showOnOpen: announcement.showOnOpen,
            sendPush: announcement.sendPush,
            publishAt: announcement.publishAt,
            expiresAt: announcement.expiresAt,
            isActive: announcement.isActive,
          }}
          onSubmit={async (payload) => {
            try {
              await updateAnnouncement.mutateAsync({
                id: announcementId,
                payload,
              });
              toast.success("ບັນທຶກຂ່າວສານແລ້ວ");
            } catch {
              toast.error("ບໍ່ສາມາດບັນທຶກຂ່າວສານໄດ້");
            }
          }}
          submitting={updateAnnouncement.isPending}
          onCancel={() => nav({ to: "/app/announcements" })}
          submitLabel="ບັນທຶກການປ່ຽນແປງ"
        />

        <div className="mt-8">
          <MarketingPushMonitorPanel
            data={pushMonitor.data}
            isLoading={pushMonitor.isLoading}
            isFetching={pushMonitor.isFetching}
            onRefresh={() => void pushMonitor.refetch()}
          />
        </div>
      </Main>
    </>
  );
}
